/**
 * What each office at a school is allowed to do.
 *
 * The offices themselves (`person_roles`) say who someone *is*; this says what
 * that is worth. Until now the answer was hard-coded: every gate in the API was
 * a literal `requireAdmin(c)`, so "admin" and "everything an admin does" were
 * the same fact and a school that wanted its IB coordinator to run the roster
 * import had to make them an administrator of the whole school - which also
 * handed them the medical records and the audit log.
 *
 * A *capability* names one of those gates. `role_permissions` rows then say, per
 * school, which offices hold which capability; the table ships empty, and with
 * no rows the answer is `defaultRoles` below - which is `['admin']` for every
 * capability, so an unconfigured school behaves exactly as it did when the gates
 * were literals. Configuring one is how a school widens a gate (give the
 * coordinator `dataImport.run`) or narrows it (take `auditLog.view` away
 * from ordinary admins).
 *
 * This module is pure and dependency-free on purpose: the API resolves gates
 * with it and the settings screen renders the matrix from it, so the two cannot
 * disagree about what a capability is called or what it does by default.
 */

/**
 * The offices a school may configure.
 *
 * `student` and `parent` are deliberately absent. What a student or a parent may
 * reach is not a school-wide switch but a question about *whose* records they
 * are - "my own", "my child's" - and that is enforced by row scoping
 * (`canAccessStudent`, `getOwnStudentFilter`), which a capability flag cannot
 * express and must not be able to override. `super_admin` is absent for the
 * opposite reason: it is the platform operator rather than an office at a
 * school, and it has never satisfied `requireAdmin` either.
 */
export const CONFIGURABLE_ROLES = ['admin', 'coordinator', 'teacher'] as const;

export type ConfigurableRole = (typeof CONFIGURABLE_ROLES)[number];

/** The sections the settings screen groups the matrix into. */
export const CAPABILITY_GROUPS = [
  'People',
  'Academic setup',
  'Student records',
  'Grades and reports',
  'School data',
] as const;

export type CapabilityGroup = (typeof CAPABILITY_GROUPS)[number];

export interface CapabilityDefinition<K extends string = string> {
  /** Stable key, stored in `role_permissions.capability`. */
  key: K;
  label: string;
  /** One sentence, shown under the label on the settings screen. */
  description: string;
  group: CapabilityGroup;
  /**
   * Who holds it when the school has said nothing. Admin-only throughout,
   * because every capability here replaces a gate that read `requireAdmin`.
   */
  defaultRoles: readonly ConfigurableRole[];
}

/**
 * One entry per admin-gated area of the app.
 *
 * A capability covers the *admin-gated* operations of its area, not everything
 * in it: reads a teacher already shares (listing terms, opening a course) are
 * gated by `requireTeacherOrAdmin` and are not what a row here changes. Taking
 * `terms.manage` away from admins stops them creating and locking terms; it does
 * not stop them seeing that terms exist.
 *
 * Two areas are deliberately absent, because a switch here has to do what its
 * label says and neither of these could yet:
 *
 *   - **The student roster.** `GET /api/students` picks its rows by office -
 *     admins get the school, teachers get their classes - and falls through to
 *     an empty list for anyone else, so an office admitted by a capability would
 *     be told the school has no students. Which rows a capability holder should
 *     see is a scoping question, not a gate.
 *   - **Health records.** `studentHealthGuard` withholds medical data by the
 *     field-permission *tier*, which is a different mechanism from this one and
 *     does not consult it. A granted office would read a silently emptied record
 *     and be refused on write.
 *
 * Both belong here once those are answered. Neither belongs here as a switch
 * that appears to grant something and does not.
 */
export const CAPABILITIES = [
  {
    key: 'people.manage',
    label: 'Manage people',
    description:
      'Add, edit and offboard the staff, students and parents in the school directory.',
    group: 'People',
    defaultRoles: ['admin'],
  },
  {
    key: 'academicYears.manage',
    label: 'Manage academic years',
    description: 'Create and edit the school year containers and their calendars.',
    group: 'Academic setup',
    defaultRoles: ['admin'],
  },
  {
    key: 'terms.manage',
    label: 'Manage terms',
    description: 'Create terms and term-courses, assign teachers, and lock grading windows.',
    group: 'Academic setup',
    defaultRoles: ['admin'],
  },
  {
    key: 'courses.manage',
    label: 'Manage courses',
    description: 'Create and edit the course catalogue.',
    group: 'Academic setup',
    defaultRoles: ['admin'],
  },
  {
    key: 'gradeSetup.manage',
    label: 'Manage grading setup',
    description: 'Edit the school grade levels and the assignment category templates.',
    group: 'Academic setup',
    defaultRoles: ['admin'],
  },
  {
    key: 'homerooms.manage',
    label: 'Manage homerooms',
    description: 'Create homerooms and decide which students and tutors belong to them.',
    group: 'Academic setup',
    defaultRoles: ['admin'],
  },
  {
    key: 'customFields.manage',
    label: 'Manage custom student fields',
    description: 'Define the extra fields the student record collects.',
    group: 'Student records',
    defaultRoles: ['admin'],
  },
  {
    key: 'studentDocuments.manage',
    label: 'Manage student documents',
    description: 'Upload, download and remove the files attached to a student.',
    group: 'Student records',
    defaultRoles: ['admin'],
  },
  {
    key: 'reportCards.manage',
    label: 'Manage report cards',
    description: 'Edit report card templates and generate report card runs.',
    group: 'Grades and reports',
    defaultRoles: ['admin'],
  },
  {
    key: 'dataImport.run',
    label: 'Import data',
    description: 'Upload rosters, enrolment forms and grade workbooks.',
    group: 'School data',
    defaultRoles: ['admin'],
  },
  {
    key: 'auditLog.view',
    label: 'View the audit log',
    description: 'Read the school-wide record of who changed what.',
    group: 'School data',
    defaultRoles: ['admin'],
  },
] as const satisfies readonly CapabilityDefinition[];

/** Every capability key, as a union. */
export type Capability = (typeof CAPABILITIES)[number]['key'];

export const CAPABILITY_KEYS: readonly Capability[] = Object.freeze(
  CAPABILITIES.map((capability) => capability.key)
);

const CAPABILITY_BY_KEY = new Map<string, CapabilityDefinition<Capability>>(
  CAPABILITIES.map((capability) => [capability.key, capability])
);

const CONFIGURABLE_ROLE_SET: ReadonlySet<string> = new Set<string>(CONFIGURABLE_ROLES);

export function isCapability(value: string): value is Capability {
  return CAPABILITY_BY_KEY.has(value);
}

export function isConfigurableRole(value: string): value is ConfigurableRole {
  return CONFIGURABLE_ROLE_SET.has(value);
}

export function getCapability(key: string): CapabilityDefinition<Capability> | undefined {
  return CAPABILITY_BY_KEY.get(key);
}

export function defaultRolesFor(key: Capability): readonly ConfigurableRole[] {
  return CAPABILITY_BY_KEY.get(key)?.defaultRoles ?? [];
}

/**
 * One stored decision: this office does, or does not, hold this capability at
 * this school. Absent means "whatever the catalogue says".
 */
export interface RolePermissionOverride {
  role: ConfigurableRole;
  capability: Capability;
  allowed: boolean;
}

/** The resolved answer for every configurable office. */
export type CapabilityMatrix = ReadonlyMap<ConfigurableRole, ReadonlySet<Capability>>;

/**
 * The catalogue defaults with the school's overrides applied.
 *
 * Rows naming a capability or a role the catalogue does not have are dropped
 * rather than trusted. Both are reachable: a capability retired in a later
 * release leaves its rows behind, and a hand-written INSERT can name any role
 * the column's enum allows. Neither may be the thing that opens a gate, so an
 * unrecognised row resolves to the default rather than to `allowed`.
 */
export function applyRolePermissionOverrides(
  overrides: readonly RolePermissionOverride[]
): CapabilityMatrix {
  const matrix = new Map<ConfigurableRole, Set<Capability>>(
    CONFIGURABLE_ROLES.map((role) => [
      role,
      new Set(
        CAPABILITIES.filter((c) =>
          (c.defaultRoles as readonly ConfigurableRole[]).includes(role)
        ).map((c) => c.key)
      ),
    ])
  );

  for (const override of overrides) {
    if (!isConfigurableRole(override.role)) continue;
    if (!isCapability(override.capability)) continue;

    const held = matrix.get(override.role)!;
    if (override.allowed) held.add(override.capability);
    else held.delete(override.capability);
  }

  return matrix;
}

export function roleHoldsCapability(
  matrix: CapabilityMatrix,
  role: string,
  capability: Capability
): boolean {
  if (!isConfigurableRole(role)) return false;
  return matrix.get(role)?.has(capability) ?? false;
}

/**
 * Does a person holding these offices hold this capability?
 *
 * The union, not the intersection: a teacher who is also a coordinator is both,
 * and every other role test in the app (`holdsRole`) reads the list the same
 * way. A person with no offices holds nothing.
 */
export function rolesHoldCapability(
  matrix: CapabilityMatrix,
  roles: readonly string[] | undefined,
  capability: Capability
): boolean {
  return (roles ?? []).some((role) => roleHoldsCapability(matrix, role, capability));
}
